import { useState, useMemo, useCallback } from 'react'

import { clsx } from 'clsx'

import {
  Card,
  DropdownMenu,
  IconButton,
  SwatchGroup,
  Text,
  PencilIcon,
  AllChannelsIcon,
  TrashIcon,
  BarChartIcon,
  EllipsisVerticalIcon,
  toast,
  UnstyledButton,
} from '@buffer-mono/popcorn'

import { colors } from '~publish/components/TagEditDialog/colors'
import type { Tag } from '~publish/gql/graphql'
import { useTags } from '~publish/hooks/useTags'
import { allChannels } from '~publish/legacy/routes'
import { getAnalyzeReportUrlForTags } from '~publish/legacy/utils/formatters/getURL'

import { DeleteConfirmationDialog } from './DeleteConfirmationDialog'

import styles from './TagItem.module.css'

interface TagItemProps {
  tag: Tag
  isAdmin: boolean
  onEditTag?: (tag: Tag) => void
}

export const TagItem = ({
  tag,
  isAdmin,
  onEditTag,
}: TagItemProps): JSX.Element => {
  const [openDropdown, setOpenDropdown] = useState(false)
  const { updateTag, deleteTag } = useTags()

  const reportUrl = useMemo(() => {
    return getAnalyzeReportUrlForTags(tag.id)
  }, [tag.id])

  const postViewUrl = useMemo(() => {
    return allChannels.getRoute({ tagIds: [tag.id] })
  }, [tag.id])

  // Only update color if it has actually changed
  const handleColorChange = useCallback(
    async (newColor: string): Promise<void> => {
      if (newColor === tag.color) return

      try {
        await updateTag({ id: tag.id, name: tag.name, color: newColor })
      } catch {
        toast.error('Failed to update tag color. Please try again later.')
      }
    },
    [tag.id, tag.name, tag.color, updateTag],
  )

  const handleDelete = useCallback(async (): Promise<void> => {
    try {
      await deleteTag(tag.id)
      toast.success(`Tag "${tag.name}" deleted successfully.`)
    } catch {
      toast.error('Failed to delete tag. Please try again later.')
    }
  }, [tag.id, tag.name, deleteTag])

  const handleColorInputChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      void handleColorChange(evt.target.value)
    },
    [handleColorChange],
  )

  return (
    <Card className={styles.tagItem}>
      {/* Color Picker */}
      <div className={styles.colorCell}>
        {isAdmin ? (
          <DropdownMenu>
            <DropdownMenu.Trigger>
              <UnstyledButton
                className={styles.colorButton}
                style={{ backgroundColor: tag.color }}
                aria-label={`Change color for ${tag.name}`}
              />
            </DropdownMenu.Trigger>
            <DropdownMenu.Content>
              <label
                htmlFor={`colorPicker-${tag.id}`}
                className={styles.colorPickerLabel}
              >
                Select Color
              </label>
              <SwatchGroup
                id={`colorPicker-${tag.id}`}
                onChange={handleColorInputChange}
                className={styles.swatchGroup}
                value={tag.color}
              >
                {colors.map(({ color, name }) => (
                  <SwatchGroup.Swatch
                    key={color}
                    color={color}
                    name={name}
                    aria-label={`color ${color}`}
                  />
                ))}
              </SwatchGroup>
            </DropdownMenu.Content>
          </DropdownMenu>
        ) : (
          <div
            className={styles.colorDot}
            style={{ backgroundColor: tag.color }}
            aria-label={`Tag color: ${tag.color}`}
          />
        )}
      </div>

      {/* Tag Name */}
      <div className={clsx(styles.nameCell, styles.grow)}>
        <Text size="lg" weight="medium">
          {tag.name}
        </Text>
      </div>

      {/* Actions */}
      <div className={clsx(styles.actionsCell)}>
        {isAdmin && (
          <IconButton
            variant="tertiary"
            label={`Edit ${tag.name}`}
            onClick={() => onEditTag?.(tag)}
          >
            <PencilIcon />
          </IconButton>
        )}

        {isAdmin && (
          <DeleteConfirmationDialog tag={tag} onDeleteConfirm={handleDelete}>
            <IconButton variant="critical-subtle" label={`Delete ${tag.name}`}>
              <TrashIcon />
            </IconButton>
          </DeleteConfirmationDialog>
        )}

        <DropdownMenu open={openDropdown} onOpenChange={setOpenDropdown}>
          <DropdownMenu.Trigger asChild>
            <IconButton
              variant="tertiary"
              label={`More actions for ${tag.name}`}
            >
              <EllipsisVerticalIcon />
            </IconButton>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content>
            <DropdownMenu.Item asChild>
              <a href={postViewUrl} target="_blank" rel="noopener noreferrer">
                <AllChannelsIcon />
                View posts
              </a>
            </DropdownMenu.Item>
            <DropdownMenu.Item asChild>
              <a href={reportUrl} target="_blank" rel="noopener noreferrer">
                <BarChartIcon />
                Open Reporting
              </a>
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu>
      </div>
    </Card>
  )
}
